import { z } from "zod";
// The answer of a validation RUN (CHT-746; docs/architecture/milestone-d-contracts.md 5.3 and 8): the structural issues plus the route and
// weather evidence, on `POST /customer/itineraries/:itineraryId/validate` (MD-I5) and `POST /staff/itineraries/:itineraryId/validate`
// (MD-I12). It is an ADD-ONLY `run` key beside the structural answer of lane I1.
//
// The customer shape holds states, observation times, the numbers the validators produced (a gap, the time that a hop needs) and a
// sentence of fixed Lafayette words per issue. It never holds a provider name, a payload, a reason code, a call count or a staff field.
// The staff shape adds the closed reason codes, the call counts and the versions of the validators.
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/u;
const uuid = z.string().regex(UUID);
const instant = z.iso.datetime({ offset: false });
const minutes = z.number().int().min(0).max(100_000);
/** A gap between two items: negative when the items overlap (PR review F5). The other minute fields are never negative. */
const gapMinutes = z.number().int().min(-100_000).max(100_000);
export const validationRunPartStateValues = ["VERIFIED", "UNVERIFIED", "NOT_APPLICABLE"];
export const validationRunEdgeStatusValues = [
    "FEASIBLE",
    "TIGHT",
    "INFEASIBLE",
    "UNVERIFIED",
];
export const validationRunTravelModeValues = ["WALK", "DRIVE", "TRANSIT"];
export const validationRunWeatherStateValues = [
    "VERIFIED",
    "UNVERIFIED",
    "OUT_OF_RANGE",
    "NOT_SENSITIVE",
];
export const validationRunWeatherLevelValues = ["NONE", "WATCH", "HIGH", "SEVERE"];
export const validationRunWeatherOutcomeValues = [
    "NONE",
    "INFO_NOTE",
    "SUGGEST_ALTERNATIVE",
    "CONCIERGE_REVIEW",
    "UNVERIFIED",
];
const customerPart = z.strictObject({
    state: z.enum(validationRunPartStateValues),
    observedAt: instant.nullable(),
});
const customerIssue = z.strictObject({
    code: z.string().min(1).max(64),
    severity: z.enum(["BLOCKING", "WARNING"]),
    /** The item the issue is about, by its stable id, or `null` for the whole plan or a day. */
    stableId: uuid.nullable(),
    /** One sentence of fixed Lafayette words for the code. Never a place, a number or a provider. */
    text: z.string().min(1).max(200),
});
const customerEdge = z.strictObject({
    fromStableId: uuid.nullable(),
    toStableId: uuid.nullable(),
    mode: z.enum(validationRunTravelModeValues),
    status: z.enum(validationRunEdgeStatusValues),
    gapMinutes: gapMinutes.nullable(),
    requiredMinutes: minutes.nullable(),
});
const customerWeather = z.strictObject({
    stableId: uuid,
    state: z.enum(validationRunWeatherStateValues),
    level: z.enum(validationRunWeatherLevelValues).nullable(),
    outcome: z.enum(validationRunWeatherOutcomeValues),
    observedAt: instant.nullable(),
});
export const customerItineraryValidationRunSchema = z.strictObject({
    /** `true`: the plan moved while the checks ran. Nothing was recorded. Read the plan again. */
    stale: z.boolean(),
    observedAt: instant.nullable(),
    evidence: z.strictObject({ route: customerPart, weather: customerPart }),
    issues: z.array(customerIssue).max(1_000),
    edges: z.array(customerEdge).max(400),
    weather: z.array(customerWeather).max(400),
    /** The items with a severe forecast: a concierge reviews them. */
    conciergeReview: z.array(uuid).max(400),
});
const staffPart = customerPart.extend({ reason: z.string().max(64).nullable() });
export const staffItineraryValidationRunSchema = z.strictObject({
    stale: z.boolean(),
    observedAt: instant.nullable(),
    evidence: z.strictObject({ route: staffPart, weather: staffPart }),
    issues: z
        .array(z.strictObject({
        code: z.string().min(1).max(64),
        severity: z.enum(["BLOCKING", "WARNING"]),
        stableId: uuid.nullable(),
    }))
        .max(1_000),
    edges: z
        .array(customerEdge.extend({
        reasonCode: z.string().max(64),
        bufferMinutes: minutes,
        travelMinutes: minutes.nullable(),
        observedAt: instant.nullable(),
    }))
        .max(400),
    weather: z
        .array(customerWeather.extend({
        sensitivity: z.enum(["NONE", "OUTDOOR", "WATER", "HEIGHT"]),
        factors: z.array(z.string().max(32)).max(10),
        reason: z.string().max(64).nullable(),
        windowStart: instant,
        windowEnd: instant,
    }))
        .max(400),
    conciergeReview: z.array(uuid).max(400),
    /** How many weather alternatives the check could offer (the operations stay with the agent tool). */
    suggestionCount: z.number().int().min(0).max(400),
    providerCalls: z.strictObject({
        routes: z.number().int().min(0).max(10_000),
        weather: z.number().int().min(0).max(10_000),
    }),
    validators: z.strictObject({
        structure: z.string().max(64),
        routes: z.string().max(64),
        weather: z.string().max(64),
        constraints: z.string().max(64),
    }),
});
